// Settings → SFTP destinations: the customer SFTP servers the Reports hub can
// upload to. Secrets are write-only; the sandbox row is fixed.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, HardDriveUpload, KeyRound, Loader2, Pencil, Plug, Plus, ShieldAlert, ShieldCheck, Trash2, XCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SftpDestinationDialog } from "./SftpDestinationDialog";

const ago = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

// Delivery receipt: the newest successful upload, and a warning when the
// newest attempt after it failed — so "is this customer current?" is one glance.
function Receipt({ d }) {
  const up = d.last_upload;
  const fail = d.last_failure;
  const failing = fail && (!up || new Date(fail.at) > new Date(up.at));
  if (!up && !fail) return <div className="mt-1 text-[11px] text-slate-400" data-testid="sftp-row-receipt">No uploads yet</div>;
  return (
    <div className="mt-1 space-y-0.5 text-[11px]" data-testid="sftp-row-receipt">
      {up && (
        <div className="flex flex-wrap items-center gap-1 text-emerald-700" title={new Date(up.at).toLocaleString()}>
          <CheckCircle2 className="h-3 w-3" /> Last upload {ago(up.at)} · <span className="font-mono text-slate-600">{up.remote_path}</span>
          <span className="text-slate-400">({up.name})</span>
        </div>
      )}
      {failing && (
        <div className="flex flex-wrap items-center gap-1 text-amber-700" title={fail.error} data-testid="sftp-row-failing">
          <XCircle className="h-3 w-3" /> Failing since {ago(fail.at)} — {fail.error}
          <Link to="/reports?tab=history&channel=sftp&status=failed" className="font-medium underline">retry in History</Link>
        </div>
      )}
    </div>
  );
}

export default function SftpDestinationsPanel() {
  const { has } = useAuth();
  const canEdit = has("settings_sftp.edit");
  const [rows, setRows] = useState(null);
  const [sandbox, setSandbox] = useState(false);
  const [editing, setEditing] = useState(null);      // null | "new" | destination
  const [deleting, setDeleting] = useState(null);
  const [testing, setTesting] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/report-hub/sftp/destinations");
      setRows(data.destinations || []);
      setSandbox(!!data.sandbox_enabled);
    } catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const test = async (d) => {
    setTesting(d.destination_id);
    try {
      const { data } = await api.post(`/report-hub/sftp/destinations/${d.destination_id}/test`);
      (data.ok ? toast.success : toast.error)(`${d.name}: ${data.message}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test failed"); }
    finally { setTesting(""); }
  };

  const remove = async () => {
    try {
      await api.delete(`/report-hub/sftp/destinations/${deleting.destination_id}`);
      toast.success(`Deleted ${deleting.name}`);
      setDeleting(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete"); }
  };

  return (
    <Card className="space-y-4 border-slate-200 p-6 shadow-sm" data-testid="settings-sftp">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-800"><HardDriveUpload className="h-5 w-5 text-primary" /> SFTP destinations</h3>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            Servers the <Link to="/reports" className="font-medium text-primary hover:underline">Reports hub</Link> can upload CSV / PDF reports to — once, or on a
            schedule. Passwords and private keys are encrypted at rest and never shown again. A failed upload raises a bell
            notification and emails everyone who can edit this tab, plus the run's owner.
          </p>
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => setEditing("new")} data-testid="sftp-add"><Plus className="h-4 w-4" /> Add destination</Button>
        )}
      </div>

      {rows === null ? (
        <div className="py-8 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="sftp-empty">
          No destinations yet.{canEdit ? " Add the first one to start uploading reports." : ""}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="sftp-list">
          {rows.map((d) => (
            <li key={d.destination_id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`sftp-row-${d.destination_id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-800" data-testid="sftp-row-name">{d.name}</span>
                  {d.is_sandbox && <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-sky-800" data-testid="sftp-row-sandbox">preview sandbox</span>}
                  <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-600">
                    {d.auth_type === "key" ? <KeyRound className="h-3 w-3" /> : null}{d.auth_type === "key" ? "SSH key" : "password"}
                  </span>
                  {d.last_test_at && (
                    <span className={`inline-flex items-center gap-1 text-[11px] ${d.last_test_ok ? "text-emerald-700" : "text-rose-700"}`}
                          title={d.last_test_message} data-testid="sftp-row-test-state">
                      {d.last_test_ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                      {d.last_test_ok ? "tested OK" : "last test failed"} · {new Date(d.last_test_at).toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 font-mono text-[12px] text-slate-500">
                  sftp://{d.username}@{d.host}:{d.port}/{(d.remote_dir || "").replace(/^\//, "")}
                  <span className="text-slate-300"> · </span><span title="File name pattern" data-testid="sftp-row-pattern">{d.filename_pattern}</span>
                </div>
                <div className="mt-0.5 text-[11px]" data-testid="sftp-row-host-key">
                  {d.host_key_fingerprint ? (
                    <span className="inline-flex items-center gap-1 text-slate-500" title={`Pinned ${d.host_key_pinned_at ? new Date(d.host_key_pinned_at).toLocaleString() : ""} — every upload checks the server presents this key before any credential is sent`}>
                      <ShieldCheck className="h-3 w-3 text-emerald-600" /> host key <span className="font-mono">{d.host_key_fingerprint}</span> pinned
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-700" title="The server's SSH key is pinned on the first successful Test or upload">
                      <ShieldAlert className="h-3 w-3" /> host key not pinned yet — press Test
                    </span>
                  )}
                </div>
                {d.last_test_message && !d.last_test_ok && <div className="text-[11px] text-rose-600">{d.last_test_message}</div>}
                <Receipt d={d} />
              </div>
              {canEdit && (
                <>
                  <Button size="sm" variant="outline" onClick={() => test(d)} disabled={testing === d.destination_id} data-testid={`sftp-test-${d.destination_id}`}>
                    {testing === d.destination_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Test
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(d)} disabled={d.is_sandbox} title={d.is_sandbox ? "The sandbox is fixed" : "Edit"}
                          data-testid={`sftp-edit-${d.destination_id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-slate-400 hover:text-rose-600" onClick={() => setDeleting(d)} disabled={d.is_sandbox}
                          title={d.is_sandbox ? "The sandbox cannot be deleted" : "Delete"} data-testid={`sftp-delete-${d.destination_id}`}><Trash2 className="h-4 w-4" /></Button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {sandbox && (
        <p className="text-[11px] text-slate-400" data-testid="sftp-sandbox-note">
          The preview sandbox is an in-process SFTP server on this pod (user <code>sandbox</code>); files land under <code>/tmp/conduit_sftp_sandbox</code>. It never runs in production.
        </p>
      )}

      {editing && (
        <SftpDestinationDialog destination={editing === "new" ? null : editing} open
                               onClose={(saved) => { setEditing(null); if (saved) load(); }} />
      )}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="sftp-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Schedules still pointing at it block the deletion; past transfer logs are kept.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sftp-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="sftp-delete-go">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
